import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { FaDatabase } from 'react-icons/fa';
import './StyledDataTable.css';


/* ── Mobile card view helper ── */
const MobileCardList = ({ columns, data, onRowClicked }) => {
  // Filter out columns marked hide='sm' (they are truly hidden on mobile)
  const visibleCols = columns.filter(col => col.hide !== 'sm');

  // Find the index column (#) if it exists, to run its cell renderer for correct page numbering
  const indexCol = columns.find(col => col.name === '#');

  return (
    <div className="sdt-mobile-cards">
      {data.length === 0 ? (
        <div className="sdt-empty-state">
          <FaDatabase className="sdt-empty-icon" />
          <p>Hakuna data iliyopatikana.</p>
        </div>
      ) : (
        data.map((row, rowIndex) => {
          // Calculate the sequence number using the original column's cell renderer if available
          const serialNumber = indexCol && indexCol.cell
            ? indexCol.cell(row, rowIndex)
            : rowIndex + 1;

          // Find the selection column (either named 'Select' or non-string JSX) and extract its checkbox value for this row
          const selectionCol = columns.find(col => col.name === 'Select' || typeof col.name !== 'string');
          const selectionCheckbox = selectionCol && selectionCol.cell
            ? selectionCol.cell(row, rowIndex)
            : null;

          return (
            <div
              key={row.id ?? rowIndex}
              className="sdt-mobile-card"
              onClick={() => onRowClicked && onRowClicked(row)}
            >
              {/* Header section with Serial Number and selection checkbox */}
              <div className="sdt-mobile-card-header">
                <span className="sdt-mobile-card-num">#{serialNumber}</span>
                {selectionCheckbox && (
                  <div className="sdt-mobile-card-select" onClick={(e) => e.stopPropagation()}>
                    {selectionCheckbox}
                  </div>
                )}
              </div>

              {visibleCols.map((col, colIndex) => {
                // Skip index column (#) and selection column since they are shown in the header
                if (col.name === '#' || col.name === 'Select' || typeof col.name !== 'string') return null;

                const value = col.cell
                  ? col.cell(row, rowIndex)
                  : col.selector
                    ? col.selector(row)
                    : null;

                // "Actions" column spans full width at the bottom
                const isActions = col.name === 'Actions' || col.name === 'Vitendo';

                return (
                  <div
                    key={colIndex}
                    className={`sdt-mobile-field${isActions ? ' sdt-mobile-actions' : ''}`}
                  >
                    {!isActions && (
                      <span className="sdt-mobile-label">{col.name}</span>
                    )}
                    <span className="sdt-mobile-value">{value}</span>
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
};


const StyledDataTable = ({
  columns,
  data,
  emptyMessage = 'Hakuna data iliyopatikana.',
  pagination = true,
  paginationPerPage = 25,
  paginationRowsPerPageOptions = [25, 50, 100, 200],
  onChangeRowsPerPage,
  paginationServer,
  paginationTotalRows,
  paginationDefaultPage,
  onChangePage,
  progressPending,
  selectableRows,
  onSelectedRowsChange,
  clearSelectedRows,
  pageInfo,
  onRowClicked,
  maxHeight,
  style,
  fixedHeader,
  fixedHeaderScrollHeight,
  ...rest
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  /* ── Empty-state component ── */
  const EmptyState = (
    <div className="sdt-empty-state">
      <FaDatabase className="sdt-empty-icon" />
      <p>{emptyMessage}</p>
    </div>
  );

  const formattedMaxHeight = maxHeight
    ? (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight)
    : undefined;

  const containerStyle = {
    ...(formattedMaxHeight ? { maxHeight: formattedMaxHeight, overflowY: 'auto', overflowX: 'hidden' } : {}),
    ...style
  };

  return (
    <div className="sdt-scroll" style={containerStyle}>
      <div className="sdt-wrapper">

        {/* ── Mobile: render card list instead of DataTable ── */}
        {isMobile ? (
          <MobileCardList
            columns={columns}
            data={data ?? []}
            onRowClicked={onRowClicked}
          />
        ) : (
          <DataTable
            columns={columns}
            data={data}
            noDataComponent={EmptyState}
            pagination={pagination}
            paginationPerPage={paginationPerPage}
            paginationRowsPerPageOptions={paginationRowsPerPageOptions}
            onChangeRowsPerPage={onChangeRowsPerPage}
            paginationServer={paginationServer}
            paginationTotalRows={paginationTotalRows}
            paginationDefaultPage={paginationDefaultPage}
            onChangePage={onChangePage}
            progressPending={progressPending}
            selectableRows={selectableRows}
            onSelectedRowsChange={onSelectedRowsChange}
            clearSelectedRows={clearSelectedRows}
            onRowClicked={onRowClicked}
            highlightOnHover
            striped
            dense
            responsive={false}
            {...rest}
          />
        )}
      </div>

      {/* Mobile pagination – simple prev/next */}
      {isMobile && pagination && (
        <div className="sdt-mobile-pagination">
          <button
            className="sdt-page-btn"
            disabled={!paginationDefaultPage || paginationDefaultPage <= 1}
            onClick={() => onChangePage && onChangePage(paginationDefaultPage - 1)}
          >
            ‹ Nyuma
          </button>
          <span className="sdt-page-num">
            Uk. {paginationDefaultPage ?? 1}
            {paginationTotalRows ? ` / ${Math.ceil(paginationTotalRows / paginationPerPage)}` : ''}
          </span>
          <button
            className="sdt-page-btn"
            disabled={
              paginationTotalRows &&
              paginationDefaultPage >= Math.ceil(paginationTotalRows / paginationPerPage)
            }
            onClick={() => onChangePage && onChangePage((paginationDefaultPage ?? 1) + 1)}
          >
            Mbele ›
          </button>
        </div>
      )}

      {pageInfo && (
        <div className="sdt-page-info text-center">{pageInfo}</div>
      )}
    </div>
  );
};

export default StyledDataTable;
